/** @jsxImportSource @emotion/react */
import { AxiosError } from "axios";
import {
  Button,
  Container,
  DateInput,
  Form,
  getUniqueId,
  Icon,
  Input,
  Link,
  Message,
  Select,
  Stack,
  Text,
  Textarea,
  VisuallyHidden,
} from "basis";
import { Separator } from "components/Separator/Separator";
import {
  validateAuMobileNumber,
  validateName,
  validateNzMobileNumber,
  validateOver18YearsOld,
  validatePostCode,
  validateSuburb,
} from "library/formValidation";
import { regionProvinceOptions, stateOptions } from "library/sharedOptions";
import { createTransactionDisputeTicket } from "library/zendeskAPI";
import { useEventTracking, usePageTracking } from "react-event-tracker";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import { typeOfDisputeOptions } from "./options";
import {
  ActionType,
  ApiGetResponse,
  FormValues,
  GetTransactionDisputeParams,
  ScreenProps,
} from "./types";

const initialValues: FormValues = {
  firstName: "",
  lastName: "",
  dateOfBirth: { day: "", month: "", year: "" },
  address: "",
  suburb: "",
  state: "",
  postCode: "",
  mobileNumber: "",
  typeOfDispute: "",
  description: "",
  transactions: {
    [getUniqueId()]: {
      date: { day: "", month: "", year: "" },
      amount: "",
      merchant: "",
    },
  },
};

function addNewTransactionUpdateFn(values) {
  const newTransactions = {
    ...values.transactions,
    [getUniqueId()]: {
      date: { day: "", month: "", year: "" },
      amount: "",
      merchant: "",
    },
  };

  return {
    ...values,
    transactions: newTransactions,
  };
}

function removeTransactionUpdateFn(id: string) {
  return (values) => {
    const newTransactions = {
      ...values.transactions,
    };

    delete newTransactions[id];

    return {
      ...values,
      transactions: newTransactions,
    };
  };
}

const MAX_TRANSACTIONS = 15;

export const TransactionDisputeForm = ({ state, dispatch }: ScreenProps) => {
  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "transaction-dispute",
      siteSubSection: "",
      pageTitle: "transaction-dispute-form",
    },
    user: {
      authenticationStatus: "logged in",
    },
  });
  const { trackEvent } = useEventTracking();

  const { accountInfo } = state;
  const token = accountInfo.cardToken;

  const transactionDisputeMutation = useMutation<
    ApiGetResponse,
    AxiosError,
    GetTransactionDisputeParams
  >(createTransactionDisputeTicket, {
    onSuccess(_ticketId) {
      trackEvent({
        event: {
          category: "application",
          action: "application-submitted",
          label: "Submit",
          location: "Transaction Dispute",
        },
      });
      dispatch({ type: ActionType.FORM_SUBMITTED });
    },
  });

  const onSubmit = ({ values, errors }) => {
    if (Object.keys(errors).length === 0) {
      transactionDisputeMutation.mutate({
        userName: accountInfo.userName,
        email: accountInfo.userEmail,
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: values.dateOfBirth,
        address: values.address,
        suburb: values.suburb,
        state: values.state,
        postCode: values.postCode,
        mobileNumber: values.mobileNumber,
        typeOfDispute: values.typeOfDispute,
        description: values.description,
        transactions: values.transactions,
        last4Digits: accountInfo.cardEndsWith || "",
        isNZ: accountInfo.isNZ,
        token,
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: { values }, setValues }) => {
        const transactionIds = Object.keys(values.transactions);
        return (
          <Stack gap="8">
            <Container bg="grey.t05" padding="4">
              <CreditCard
                cardName={accountInfo.cardName}
                cardEndsWith={accountInfo.cardEndsWith}
              />
            </Container>
            <Stack gap="8" width-sm="50%">
              <Stack gap="3">
                <Text>
                  <strong>Email address</strong>
                </Text>
                <Text>{accountInfo.userEmail}</Text>
                <Text textStyle="body2">
                  Your letter will be emailed to this address.
                </Text>
              </Stack>
              <Input
                name="firstName"
                label="First name"
                validate={validateName}
              />
              <Input
                name="lastName"
                label="Last name"
                validate={validateName}
              />
              <DateInput
                name="dateOfBirth"
                label="Date of birth"
                validate={validateOver18YearsOld}
              />
              <Input
                type="tel"
                name="mobileNumber"
                label="Mobile number"
                validate={
                  accountInfo.isNZ
                    ? validateNzMobileNumber
                    : validateAuMobileNumber
                }
              />
              <Input name="address" label="Address" />
              <Input name="suburb" label="Suburb" validate={validateSuburb} />
              <Select
                name="state"
                label={accountInfo.isNZ ? "Province/Region" : "State"}
                options={
                  accountInfo.isNZ ? regionProvinceOptions : stateOptions
                }
              />
              <Input
                name="postCode"
                label="Postcode"
                validate={validatePostCode}
                maxLength="4"
              />
              <Select
                name="typeOfDispute"
                label="Type of dispute"
                options={typeOfDisputeOptions}
              />
              <Textarea
                name="description"
                label="Description"
                height="100"
                helpText="Please enter the details of your request. A member of our support staff will respond as soon as possible."
              />
              {transactionIds.map((id, index) => (
                <Container padding="5" hasBorder key={id}>
                  <Stack gap="4">
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text textStyle="subtitle2">
                        <strong>{`Transaction ${index + 1}`}</strong>
                      </Text>
                      {transactionIds.length > 1 && (
                        <Button
                          variant="icon"
                          onClick={() => {
                            setValues(removeTransactionUpdateFn(id));
                          }}
                        >
                          <Icon name="trash" />
                          <VisuallyHidden>Remove transaction</VisuallyHidden>
                        </Button>
                      )}
                    </div>
                    <Separator />
                    <Stack gap="8">
                      <DateInput
                        name={`transactions.${id}.date`}
                        label="Date"
                      />
                      <Input
                        name={`transactions.${id}.amount`}
                        variant="decimal"
                        prefix="$"
                        label="Amount"
                      />
                      <Input
                        name={`transactions.${id}.merchant`}
                        label="Merchant"
                      />
                    </Stack>
                  </Stack>
                </Container>
              ))}
              {transactionIds.length < MAX_TRANSACTIONS && (
                <Link
                  href="#"
                  newTab={false}
                  onClick={(e) => {
                    e.preventDefault();
                    setValues(addNewTransactionUpdateFn);
                  }}
                >
                  Add another transaction
                </Link>
              )}
              {transactionDisputeMutation.isError && (
                <Message severity="blocking" bg="secondary.pink.t30">
                  Sorry, there was an error submitting your dispute. Please try
                  again later.
                </Message>
              )}
              <Button
                type="submit"
                width="160"
                loading={transactionDisputeMutation.isLoading}
              >
                Submit
              </Button>
            </Stack>
          </Stack>
        );
      }}
    </Form>
  );
};
